import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useEffectiveFeatures, useFeatureEnabled } from "@/lib/features";
import { branchesApi } from "@/lib/api/branches";
import { qk } from "@/lib/query-client";
import { useAuthStore } from "@/stores/auth-store";
import { authApi } from "@/lib/api/auth";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";
import { Button } from "@/components/ui/Button";
import { Icon } from "./Icon";

const ACTIVE_BRANCHES_QUERY = { status: "active" as const, per_page: 100 };

/**
 * Multi-Branch Tenant Admin gate: sits between ProtectedRoute and AppShell,
 * wrapping the entire tenant workspace. A no-op (immediate Outlet, no query)
 * for every role except "tenant_admin" - Manager/HR/Employee/Branch
 * Manager/Super Admin's own navigation and route behaviour are completely
 * unaffected, whether or not a stale X-Branch-ID header exists client-side
 * (the backend already ignores it for any role but tenant_admin too).
 *
 * For a Tenant Admin: 0 active branches -> an inline empty state, not a
 * redirect loop. Exactly 1 -> silently auto-selected, never shown the
 * picker. 2+ -> the persisted activeBranch is re-validated against the
 * tenant's CURRENT branch list on every mount (closes the "a branch was
 * deactivated/deleted after selection" gap) - unset or no-longer-present
 * sends them to /tenant/branch-selection; a still-valid selection passes
 * straight through.
 *
 * Branch Management itself (/branches/*) is always exempt, at any branch
 * count including zero - it's the one tool that can fix a "no active
 * branches" state, so gating it behind already having an active branch is a
 * dead end with no way out. The exemption is purely route-scoped: navigating
 * anywhere else while still branchless re-blocks immediately.
 */
export function RequireActiveBranch() {
  const user = useAuthStore((s) => s.user);
  const activeBranch = useAuthStore((s) => s.activeBranch);
  const setActiveBranch = useAuthStore((s) => s.setActiveBranch);
  const clearActiveBranch = useAuthStore((s) => s.clearActiveBranch);
  const queryClient = useQueryClient();
  const location = useLocation();

  const isTenantAdmin = user?.role === "tenant_admin" && !user.is_super_admin;
  const isBranchManagementRoute = location.pathname.startsWith("/branches");
  const branchesEnabled = useFeatureEnabled("branch_management");
  const { isLoading: featuresLoading } = useEffectiveFeatures();

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.branches.list(ACTIVE_BRANCHES_QUERY),
    queryFn: () => branchesApi.list(ACTIVE_BRANCHES_QUERY),
    enabled: isTenantAdmin && branchesEnabled,
  });
  const branches = data?.items ?? [];
  const stillValid = activeBranch && branches.some((b) => b.id === activeBranch.id);

  useEffect(() => {
    if (isTenantAdmin && branchesEnabled && branches.length === 1 && !stillValid) {
      setActiveBranch(branches[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTenantAdmin, branchesEnabled, branches.length, stillValid]);

  // With Branch Management off there is no switcher left, so a saved selection
  // would keep narrowing every request to that one branch.
  useEffect(() => {
    if (!branchesEnabled && activeBranch) {
      clearActiveBranch();
      queryClient.invalidateQueries();
    }
  }, [branchesEnabled, activeBranch, clearActiveBranch, queryClient]);

  // Features read as on until the list loads, so hold the workspace until then:
  // otherwise a page for a turned-off feature mounts and calls a refused API.
  if (featuresLoading) return <FullPageSpinner />;
  if (!isTenantAdmin || !branchesEnabled || isBranchManagementRoute) return <Outlet />;
  if (isLoading) return <FullPageSpinner />;
  // A transient load failure degrades to "no active branch narrowing" rather
  // than trapping the Tenant Admin outside their own workspace - the real
  // RBAC boundary is enforced server-side regardless of this gate, so
  // failing open here only ever costs a UX narrowing, never real access.
  if (isError) return <Outlet />;

  if (branches.length === 0) {
    return <NoActiveBranchesScreen />;
  }
  if (branches.length === 1) {
    // The effect above sets it; render the spinner for this one tick rather
    // than flashing the picker for a tenant that will never actually need it.
    return stillValid ? <Outlet /> : <FullPageSpinner />;
  }
  if (!stillValid) {
    return <Navigate to="/tenant/branch-selection" replace />;
  }
  return <Outlet />;
}

/** The 0-active-branches dead end - a real way out (Branch Management, which
 * RequireActiveBranch exempts from this same gate above), not just
 * instructional text with nowhere to click. */
function NoActiveBranchesScreen() {
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <EmptyState
        icon={<Icon name="building" className="h-7 w-7" />}
        title="No active branches yet"
        description="Your organization has no active branches configured. Create one from Branch Management to continue, or contact your tenant administrator to get set up."
        action={
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate("/branches")}>
              <Icon name="plus" className="h-4 w-4" />
              Go to Branch Management
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                authApi.logout().catch(() => {});
                signOut();
                navigate("/login", { replace: true });
              }}
            >
              Sign out
            </Button>
          </div>
        }
      />
    </div>
  );
}
